import { Socket } from 'socket.io-client';
import { logger } from './logger';

const STUN_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'stun:stun3.l.google.com:19302' },
  { urls: 'stun:stun4.l.google.com:19302' }
];

export interface CallSignal {
  type: 'offer' | 'answer' | 'ice-candidate' | 'call-request' | 'call-accept' | 'call-reject' | 'call-rejected' | 'call-end' | 'call-delivered';
  roomId: string;
  offer?: RTCSessionDescriptionInit;
  answer?: RTCSessionDescriptionInit;
  candidate?: RTCIceCandidateInit;
  from?: string;
  to?: string;
  fromName?: string;
  fromPhoto?: string;
  toName?: string;
  toPhoto?: string;
  isVideoCall?: boolean; // true for video call, false for audio only
  reason?: string; // Rejection reason for call-reject/call-rejected
}

export class WebRTCService {
  private socket: Socket | null = null;
  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  public remoteStream: MediaStream | null = null;
  private roomId: string = '';
  private incomingCallCallback: ((signal: CallSignal) => void) | null = null;
  private outgoingCallCallback: ((signal: CallSignal) => void) | null = null;
  private callAcceptedCallback: (() => void) | null = null;
  private callRejectedCallback: ((reason?: string) => void) | null = null;
  private callEndedCallback: (() => void) | null = null;
  private callDeliveredCallback: (() => void) | null = null;
  private isListenersSetup: boolean = false;
  private currentSocketId: string = '';
  private currentCallInfo: { to: string; name: string; photo?: string; isVideoCall?: boolean } | null = null;
  private pendingOffer: RTCSessionDescriptionInit | null = null;
  private isVideoCall: boolean = true; // Default to video call
  private otherPeerId: string = ''; // Track the other peer's userId for signal routing
  
  // Use existing socket instead of creating new one
  setSocket(socket: Socket | null) {
    logger.log('📞🔥🔥🔥 [WEBRTC] ============================================');
    logger.log('📞🔥🔥🔥 [WEBRTC] SETTING SOCKET');
    logger.log('📞🔥🔥🔥 [WEBRTC] ============================================');
    logger.log('📞🔥 [WEBRTC] New socket ID:', socket?.id);
    logger.log('📞🔥 [WEBRTC] Socket connected:', socket?.connected);
    logger.log('📞🔥 [WEBRTC] Previous socket ID:', this.currentSocketId);
    logger.log('📞🔥 [WEBRTC] Previous listeners setup:', this.isListenersSetup);
    
    // Remove old listeners if socket exists
    if (this.socket) {
      logger.log('📞🔥 [WEBRTC] Removing old listeners from previous socket');
      this.socket.off('call-signal');
      this.isListenersSetup = false;
    }
    
    // Update current socket and ID
    this.socket = socket;
    this.currentSocketId = socket?.id || '';
    
    // Setup listeners when socket is provided and connected
    if (socket && socket.connected) {
      logger.log('📞🔥 [WEBRTC] Setting up socket listeners now');
      this.setupSocketListeners();
      this.isListenersSetup = true;
      logger.log('✅🔥 [WEBRTC] Socket listeners setup complete');
      
      // 🔥 FIX: Request pending call check from backend after listeners are ready
      setTimeout(() => {
        logger.log('📞🔔 [WEBRTC] Requesting backend to check for pending calls...');
        socket.emit('check_pending_calls');
      }, 100);
    } else if (socket && !socket.connected) {
      logger.log('⚠️🔥 [WEBRTC] Socket provided but not connected yet - will set up on connect');
    }
    
    logger.log('📞🔥🔥🔥 [WEBRTC] ============================================');
  }

  private setupSocketListeners() {
    if (!this.socket) return;

    logger.log('📞 [WEBRTC] Setting up socket listeners');
    logger.log('📞 [WEBRTC] Socket ID:', this.socket.id);
    logger.log('📞 [WEBRTC] Socket connected:', this.socket.connected);

    this.socket.on('call-signal', async (signal: CallSignal) => {
      logger.log('📞🔥 [WEBRTC] ========================================');
      logger.log('📞🔥 [WEBRTC] CALL-SIGNAL RECEIVED:', signal.type);
      logger.log('📞🔥 [WEBRTC] ========================================');
      logger.log('📞🔥 [WEBRTC] Full signal:', JSON.stringify(signal, null, 2));
      
      switch (signal.type) {
        case 'call-request':
          logger.log('🔔🔥 [WEBRTC] INCOMING CALL REQUEST FROM:', signal.fromName);
          logger.log('🔔🔥 [WEBRTC] Room ID:', signal.roomId);
          logger.log('🔔🔥 [WEBRTC] From user:', signal.from);
          logger.log('🔔🔥 [WEBRTC] Has callback?', !!this.incomingCallCallback);
          
          this.roomId = signal.roomId; // Store room ID
          if (signal.from) {
            this.otherPeerId = signal.from; // Store caller's ID for future signaling
          }
          
          if (this.incomingCallCallback) {
            logger.log('🔔🔥 [WEBRTC] Triggering incoming call callback');
            this.incomingCallCallback(signal);
            logger.log('🔔🔥 [WEBRTC] Incoming call callback triggered');
          } else {
            console.error('❌ [WEBRTC] NO INCOMING CALL CALLBACK REGISTERED!');
          }
          break;
        
        case 'call-accept':
          logger.log('✅ [WEBRTC] Call accepted');
          this.roomId = signal.roomId; // Ensure room ID is set
          if (this.callAcceptedCallback) {
            this.callAcceptedCallback();
          }
          break;
        
        case 'call-reject':
        case 'call-rejected':
          logger.log('❌ [WEBRTC] Call rejected');
          logger.log('❌ [WEBRTC] Rejection reason:', signal.reason || 'No reason provided');
          if (this.callRejectedCallback) {
            this.callRejectedCallback(signal.reason);
          }
          break;
        
        case 'call-end':
          logger.log('👋 [WEBRTC] Call ended');
          if (this.callEndedCallback) {
            this.callEndedCallback();
          }
          break;
        
        case 'call-delivered':
          logger.log('✅ [WEBRTC] Call delivered to recipient - they are seeing the modal');
          if (this.callDeliveredCallback) {
            this.callDeliveredCallback();
          }
          break;
        
        default:
          // Handle WebRTC signaling (offer, answer, ice-candidate)
          await this.handleSignal(signal);
      }
    });
    
    logger.log('✅ [WEBRTC] Socket listeners setup complete');
  }

  async initializePeerConnection(roomId: string, isVideoCall: boolean = true): Promise<RTCPeerConnection> {
    this.roomId = roomId;
    this.isVideoCall = isVideoCall;
    
    logger.log('📞 [WEBRTC] Initializing peer connection for room:', roomId);
    logger.log('📞 [WEBRTC] Is video call:', isVideoCall);
    
    this.peerConnection = new RTCPeerConnection({
      iceServers: STUN_SERVERS
    });

    // Get local video and audio stream
    try {
      logger.log('📞 [WEBRTC] Requesting', isVideoCall ? 'camera and microphone' : 'microphone only', 'access...');
      this.localStream = await navigator.mediaDevices.getUserMedia({ 
        audio: true, 
        video: isVideoCall ? { width: 1280, height: 720 } : false
      });
      logger.log('📞 [WEBRTC]', isVideoCall ? 'Camera and microphone' : 'Microphone', 'access granted');
      
      this.localStream.getTracks().forEach(track => {
        logger.log('📞 [WEBRTC] Adding local track to peer connection:', track.kind);
        this.peerConnection!.addTrack(track, this.localStream!);
      });
    } catch (error) {
      console.error('❌ [WEBRTC] Error accessing camera/microphone:', error);
      throw new Error('Could not access camera/microphone');
    }

    // Handle remote stream
    this.peerConnection.ontrack = (event) => {
      logger.log('📞 [WEBRTC] Received remote track:', event.track.kind);
      if (event.streams && event.streams[0]) {
        this.remoteStream = event.streams[0];
        logger.log('📞 [WEBRTC] Remote stream set:', this.remoteStream.id);
      }
    };

    // Handle ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.socket) {
        logger.log('📞 [WEBRTC] New ICE candidate, sending to peer');
        this.socket.emit('call-signal', {
          type: 'ice-candidate',
          roomId: this.roomId,
          candidate: event.candidate.toJSON()
        });
      }
    };

    // Monitor connection state
    this.peerConnection.onconnectionstatechange = () => {
      logger.log('📞 [WEBRTC] Connection state:', this.peerConnection?.connectionState);
    };

    this.peerConnection.oniceconnectionstatechange = () => {
      logger.log('📞 [WEBRTC] ICE connection state:', this.peerConnection?.iceConnectionState);
    };

    // Process pending offer if exists (for incoming calls)
    if (this.pendingOffer) {
      logger.log('📞 [WEBRTC] Processing pending offer');
      const offer = this.pendingOffer;
      this.pendingOffer = null;
      
      setTimeout(async () => {
        try {
          await this.peerConnection!.setRemoteDescription(new RTCSessionDescription(offer));
          const answer = await this.peerConnection!.createAnswer();
          await this.peerConnection!.setLocalDescription(answer);
          
          if (this.socket) {
            logger.log('📞 [WEBRTC] Sending answer to pending offer');
            this.socket.emit('call-signal', {
              type: 'answer',
              roomId: this.roomId,
              answer
            });
          }
        } catch (error) {
          console.error('❌ [WEBRTC] Error processing pending offer:', error);
        }
      }, 100);
    }

    return this.peerConnection;
  }

  async createOffer(): Promise<RTCSessionDescriptionInit> {
    if (!this.peerConnection) {
      throw new Error('Peer connection not initialized');
    }

    logger.log('📞 [WEBRTC] Creating offer...');
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    logger.log('📞 [WEBRTC] Offer created');

    if (this.socket) {
      this.socket.emit('call-signal', {
        type: 'offer',
        roomId: this.roomId,
        offer
      });
    }

    return offer;
  }

  async handleSignal(signal: CallSignal) {
    try {
      switch (signal.type) {
        case 'offer':
          if (signal.offer) {
            logger.log('📞 [WEBRTC] Received offer');
            
            // If we don't have a peer connection yet, store the offer
            if (!this.peerConnection) {
              logger.log('📞 [WEBRTC] Storing offer for later (peer connection not ready)');
              this.pendingOffer = signal.offer;
              this.roomId = signal.roomId;
              return;
            }
            
            logger.log('📞 [WEBRTC] Processing offer');
            await this.peerConnection.setRemoteDescription(new RTCSessionDescription(signal.offer));
            const answer = await this.peerConnection.createAnswer();
            await this.peerConnection.setLocalDescription(answer);
            
            if (this.socket) {
              logger.log('📞 [WEBRTC] Sending answer');
              this.socket.emit('call-signal', {
                type: 'answer',
                roomId: signal.roomId,
                answer
              });
            }
          }
          break;

        case 'answer':
          if (signal.answer && this.peerConnection) {
            logger.log('📞 [WEBRTC] Processing answer');
            await this.peerConnection.setRemoteDescription(new RTCSessionDescription(signal.answer));
          }
          break;

        case 'ice-candidate':
          if (signal.candidate) {
            if (!this.peerConnection) {
              console.warn('⚠️ [WEBRTC] Received ICE candidate but no peer connection');
              return;
            }
            logger.log('📞 [WEBRTC] Adding ICE candidate');
            await this.peerConnection.addIceCandidate(new RTCIceCandidate(signal.candidate));
          }
          break;
      }
    } catch (error) {
      console.error('❌ [WEBRTC] Error handling signal:', error);
    }
  }

  sendCallRequest(to: string, fromName: string, fromPhoto?: string, roomId?: string, toName?: string, toPhoto?: string, isVideoCall: boolean = true, fromUserId?: string) {
    if (!this.socket) {
      console.error('❌ [WEBRTC] Socket not connected');
      return;
    }

    // Use provided roomId or the current one
    const callRoomId = roomId || this.roomId;
    this.roomId = callRoomId;
    this.isVideoCall = isVideoCall;
    this.otherPeerId = to; // Store the other peer's ID for signaling

    // Store call info for outgoing call UI
    this.currentCallInfo = {
      to,
      name: toName || 'Unknown',
      photo: toPhoto,
      isVideoCall
    };

    // Get caller's userId from parameter or localStorage
    let userId = fromUserId;
    if (!userId) {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          userId = user._id;
        }
      } catch (error) {
        console.error('❌ [WEBRTC] Error parsing user from localStorage:', error);
      }
    }
    
    if (!userId) {
      console.error('❌ [WEBRTC] No userId available');
      return;
    }

    const payload = {
      type: 'call-request',
      from: userId, // Add caller's userId
      to,
      roomId: callRoomId,
      fromName,
      fromPhoto,
      toName,
      toPhoto,
      isVideoCall
    };
    
    logger.log('📞 [WEBRTC] Sending', isVideoCall ? 'VIDEO' : 'AUDIO', 'call request:', payload);
    this.socket.emit('call-signal', payload);
    
    // Trigger outgoing call callback
    if (this.outgoingCallCallback) {
      this.outgoingCallCallback({
        ...payload,
        toName,
        toPhoto,
        isVideoCall
      });
    }
  }

  async acceptCall(roomId: string, userId?: string, callerId?: string) {
    if (this.socket) {
      logger.log('📞 [WEBRTC] Accepting call:', roomId);
      this.roomId = roomId;
      
      let finalUserId = userId;
      if (!finalUserId) {
        try {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const user = JSON.parse(userStr);
            finalUserId = user._id;
          }
        } catch (error) {
          console.error('❌ [WEBRTC] Error parsing user from localStorage:', error);
        }
      }
      
      if (!finalUserId) {
        console.error('❌ [WEBRTC] No userId available');
        return;
      }
      
      logger.log('✅ [WEBRTC] Sending call-accept with userId:', finalUserId, 'to:', callerId);
      
      // Send accept signal to backend with from/to for proper routing
      this.socket.emit('call-signal', {
        type: 'call-accept',
        roomId,
        from: finalUserId,
        to: callerId // Add the caller's ID so backend knows who to notify
      });
    }
  }

  rejectCall(roomId: string, to?: string, reason?: string) {
    if (this.socket) {
      logger.log('📞 [WEBRTC] Rejecting call:', roomId, 'Reason:', reason);
      this.socket.emit('call-signal', {
        type: 'call-reject',
        roomId,
        to,
        reason
      });
    }
  }

  notifyCallDelivered(roomId: string, to: string) {
    if (this.socket) {
      logger.log('📞 [WEBRTC] Notifying call delivered:', roomId);
      this.socket.emit('call-signal', {
        type: 'call-delivered',
        roomId,
        to
      });
    }
  }

  endCall() {
    if (this.socket && this.roomId) {
      logger.log('📞 [WEBRTC] Ending call:', this.roomId);
      this.socket.emit('call-signal', {
        type: 'call-end',
        roomId: this.roomId
      });
    }
    this.cleanup();
  }

  cleanup() {
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }

    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.remoteStream = null;
    this.roomId = '';
    this.currentCallInfo = null;
    this.pendingOffer = null;
  }

  getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  setLocalStream(stream: MediaStream | null) {
    this.localStream = stream;
    logger.log('📞 [WEBRTC] Local stream updated:', !!stream);
  }

  getPeerConnection(): RTCPeerConnection | null {
    return this.peerConnection;
  }

  isSocketConnected(): boolean {
    return this.socket?.connected || false;
  }

  onIncomingCall(callback: (signal: CallSignal) => void) {
    this.incomingCallCallback = callback;
    logger.log('📞 [WEBRTC] Incoming call callback registered');
  }

  onOutgoingCall(callback: (signal: CallSignal) => void) {
    this.outgoingCallCallback = callback;
    logger.log('📞 [WEBRTC] Outgoing call callback registered');
  }

  onCallAccepted(callback: () => void) {
    this.callAcceptedCallback = callback;
  }

  onCallRejected(callback: (reason?: string) => void) {
    this.callRejectedCallback = callback;
  }

  onCallEnded(callback: () => void) {
    this.callEndedCallback = callback;
  }

  onCallDelivered(callback: () => void) {
    this.callDeliveredCallback = callback;
    logger.log('📞 [WEBRTC] Call delivered callback registered');
  }

  getCurrentCallInfo() {
    return this.currentCallInfo;
  }

  clearCurrentCallInfo() {
    this.currentCallInfo = null;
  }

  getRoomId() {
    return this.roomId;
  }

  getIsVideoCall() {
    return this.isVideoCall;
  }
}

export const webrtcService = new WebRTCService();